import * as SelectPrimitive from "@radix-ui/react-select";
import * as React from "react";

import { cn } from "@/lib/chillnn/cn";

import { SelectScrollButton } from "./select-scroll-button";

const SelectContent = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => {
	const viewportRef = React.useRef<HTMLDivElement>(null);

	// Radix UIのデフォルトアイテム高さ
	const DEFAULT_ITEM_HEIGHT = 32;

	const getItemHeight = React.useCallback(() => {
		if (!viewportRef.current) return DEFAULT_ITEM_HEIGHT;
		const firstItem = viewportRef.current.querySelector("[data-radix-select-item]") as HTMLElement;
		return firstItem?.offsetHeight ?? DEFAULT_ITEM_HEIGHT;
	}, []);

	const handleScrollUp = React.useCallback(() => {
		if (!viewportRef.current) return;
		const itemHeight = getItemHeight();
		viewportRef.current.scrollBy({ top: -itemHeight, behavior: "smooth" });
	}, [getItemHeight]);

	const handleScrollDown = React.useCallback(() => {
		if (!viewportRef.current) return;
		const itemHeight = getItemHeight();
		viewportRef.current.scrollBy({ top: itemHeight, behavior: "smooth" });
	}, [getItemHeight]);

	// 10個以上の子要素がある場合はスクロールボタンを表示
	const shouldShowScrollButtons = React.Children.count(children) > 10;

	return (
		<SelectPrimitive.Portal>
			<SelectPrimitive.Content
				ref={ref}
				className={cn(
					/**
					 * z-index に 110を当てている理由
					 * - dialog内にselect-boxをおいた時にdialogの下に選択肢が表示されてしまうため
					 * - 他に良い方法があれば修正してください！
					 */
					"relative z-[110] min-w-[8rem] overflow-hidden rounded-md border border-zinc-200 bg-white text-zinc-950 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
					position === "popper" &&
						"data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
					className,
				)}
				position={position}
				{...props}
			>
				{shouldShowScrollButtons && (
					<div className="flex h-8 w-full items-center justify-center">
						<SelectPrimitive.ScrollUpButton asChild>
							<SelectScrollButton direction="up" onClick={handleScrollUp} />
						</SelectPrimitive.ScrollUpButton>
					</div>
				)}
				<SelectPrimitive.Viewport
					ref={viewportRef}
					className={cn(
						"max-h-80 overflow-y-auto p-1",
						position === "popper" && "w-full min-w-[var(--radix-select-trigger-width)]",
					)}
				>
					{children}
				</SelectPrimitive.Viewport>
				{shouldShowScrollButtons && (
					<div className="flex h-8 w-full items-center justify-center">
						<SelectPrimitive.ScrollDownButton asChild>
							<SelectScrollButton direction="down" onClick={handleScrollDown} />
						</SelectPrimitive.ScrollDownButton>
					</div>
				)}
			</SelectPrimitive.Content>
		</SelectPrimitive.Portal>
	);
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

export { SelectContent };
